// Editing, creating and deleting records: PATCH/POST/DELETE /api/objects/...
// Which fields are writable, and how, comes from the server (GET /meta's
// `writable`), so the record page, the New dialog and the server never
// disagree. Request building and value conversion live here, free of React,
// so they can be unit tested (writes.test.ts).

import { type ColumnDef, type FieldCondition, type FieldDef, type FieldDependency, type FieldEditorDef, type FilterOption, labelFromKey, type ObjectMetadata, type RecordFormField } from '@olytic/foundation';

import type { ObjectKey } from '../navigation';
import { humanize } from './format';
import { type ApiFilter, ObjectsApiError, type ObjectRow } from './objectsApi';

export type WritableType = 'text' | 'number' | 'date' | 'boolean' | 'picklist' | 'lookup';

export interface WritableField {
  key: string;
  type: WritableType;
  required?: boolean;
  options?: string[];
  min?: number;
  max?: number;
  maxLength?: number;
  /** `filters` narrow what the editor offers (an Owner: active people only); the server checks the same. */
  lookup?: { object: string; filters?: ApiFilter[] };
  /** A unique constraint covers it, so a clone leaves it blank. */
  unique?: boolean;
  /** Set once the record exists (an opportunity's primary quote): never on a New or Clone form. */
  editOnly?: boolean;
  /** Long notes: a New or Edit form gives it a Textarea with dictation. */
  multiline?: boolean;
  /** Shown only while another field holds one of these values. */
  visibleWhen?: FieldCondition;
  /** Required only while another field holds one of these values; the server checks it too. */
  requiredWhen?: FieldCondition;
  /** A dependent picklist: the values allowed for each value of another picklist. */
  dependsOn?: FieldDependency;
}

export interface Writable {
  create: boolean;
  delete: boolean;
  fields: WritableField[];
}

export const NOT_WRITABLE: Writable = { create: false, delete: false, fields: [] };

/** What a New or Clone form may write: every field but those set once the record exists. */
export const creatable = (writable: Writable): Writable => ({ ...writable, fields: writable.fields.filter((w) => !w.editOnly) });

const TYPES = new Set(['text', 'number', 'date', 'boolean', 'picklist', 'lookup']);
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The `writable` part of a /meta payload; anything malformed reads as not writable. */
export function readWritable(payload: unknown): Writable {
  if (!isRecord(payload) || !isRecord(payload.writable)) return NOT_WRITABLE;
  const w = payload.writable;
  if (!Array.isArray(w.fields)) return NOT_WRITABLE;
  const fields = w.fields.filter(
    (f): f is WritableField => isRecord(f) && typeof f.key === 'string' && typeof f.type === 'string' && TYPES.has(f.type),
  );
  return { create: w.create === true, delete: w.delete === true, fields };
}

/** The server spells table keys with underscores; routes use hyphens. */
export function objectKeyOf(lookupObject: string): ObjectKey {
  return lookupObject.replace(/_/g, '-') as ObjectKey;
}

const YES_NO: FilterOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/** Finds records of another object for a lookup editor, within `filters` when given. */
export type LoadOptions = (object: ObjectKey, filters?: ApiFilter[]) => (query: string) => Promise<FilterOption[]>;

/**
 * What every lookup editor also offers, set once by the app shell: the
 * person's recently viewed records of the object, and "+ New" to create one.
 */
export const lookupServices: {
  recent?: (object: ObjectKey) => () => Promise<FilterOption[]>;
  create?: (object: ObjectKey) => Promise<FilterOption | null>;
  singularOf?: (object: ObjectKey) => string;
} = {};

/** The library editor for one writable field. */
/** A text field for long notes: marked multiline, or allowed a thousand characters or more. */
export function isLongText(field: WritableField): boolean {
  return field.multiline === true || (field.maxLength !== undefined && field.maxLength >= 1000);
}

export function editorFor(field: WritableField, loadOptions: LoadOptions): FieldEditorDef<ObjectRow> {
  const nullable = !field.required;
  switch (field.type) {
    case 'number':
      return {
        kind: 'number',
        ...(field.min !== undefined ? { min: field.min } : {}),
        ...(field.max !== undefined ? { max: field.max } : {}),
        nullable,
      };
    case 'date':
      // A timestamp column edited as a date (an activity's occurred_at) arrives in full.
      return {
        kind: 'date',
        nullable,
        getValue: (row) => {
          const value = row[field.key];
          return value === null || value === undefined ? '' : String(value).slice(0, 10);
        },
      };
    case 'boolean':
      return { kind: 'select', options: YES_NO, nullable };
    case 'picklist':
      return {
        kind: 'select',
        options: (field.options ?? []).map((v) => ({ value: v, label: humanize(v) })),
        nullable,
        ...(field.dependsOn ? { dependsOn: field.dependsOn } : {}),
      };
    case 'lookup': {
      const target = objectKeyOf(field.lookup?.object ?? '');
      const { recent, create, singularOf } = lookupServices;
      return {
        kind: 'lookup',
        nullable,
        loadOptions: loadOptions(target, field.lookup?.filters),
        ...(recent ? { loadRecent: recent(target) } : {}),
        ...(create ? { onCreate: () => create(target), createLabel: `New ${singularOf?.(target) ?? 'record'}` } : {}),
        getValue: (row) => {
          const id = row[field.key];
          return id === null || id === undefined ? '' : String(id);
        },
      };
    }
    default:
      return {
        kind: 'text',
        ...(field.maxLength !== undefined ? { maxLength: field.maxLength } : {}),
        // Long notes and descriptions edit in a Textarea on the record page that can be dragged taller.
        ...(isLongText(field) ? { multiline: true } : {}),
        nullable,
      };
  }
}

/** The writable field a detail field or list column edits: its own key, or for a lookup, the id it links through. */
export function writableFor(field: Pick<FieldDef<ObjectRow>, 'key' | 'link'>, writable: Writable): WritableField | undefined {
  return writable.fields.find((w) => w.key === field.key) ?? (field.link ? writable.fields.find((w) => w.key === field.link?.idKey) : undefined);
}

/** The record page's metadata with an editor on every field the server lets people change. */
export function withEditors(
  metadata: ObjectMetadata<ObjectRow>,
  writable: Writable,
  loadOptions: LoadOptions,
): ObjectMetadata<ObjectRow> {
  if (writable.fields.length === 0) return metadata;
  const edit = (field: FieldDef<ObjectRow>): FieldDef<ObjectRow> => {
    const w = writableFor(field, writable);
    return w ? { ...field, ...rulesOf(w), editor: editorFor(w, loadOptions) } : field;
  };
  return metadata.details ? { ...metadata, details: metadata.details.map(edit) } : metadata;
}

/** A writable field's rules on other fields, for a detail or form field. */
function rulesOf(w: WritableField): Pick<FieldDef<ObjectRow>, 'visibleWhen' | 'requiredWhen'> {
  return {
    ...(w.visibleWhen ? { visibleWhen: w.visibleWhen } : {}),
    ...(w.requiredWhen ? { requiredWhen: w.requiredWhen } : {}),
  };
}

/**
 * List columns with an editor on every field the server lets people change,
 * so a cell can be edited in place (double-click). The name column stays a
 * link to the record.
 */
export function withColumnEditors(
  columns: ColumnDef<ObjectRow>[],
  writable: Writable,
  loadOptions: LoadOptions,
): ColumnDef<ObjectRow>[] {
  if (writable.fields.length === 0) return columns;
  return columns.map((column) => {
    // A column that says it is not editable stays read-only (an opportunity's primary quote, picked on its page).
    if (column.primary || column.editable === false) return column;
    const w = writableFor(column, writable);
    return w ? { ...column, editor: editorFor(w, loadOptions), editable: true } : column;
  });
}

/**
 * A join row's columns (a contact's role on a deal) with an editor on its own
 * fields: a yes/no is a checkbox that saves at once, and lookups stay links.
 */
export function joinColumns(
  columns: ColumnDef<ObjectRow>[],
  writable: Writable,
  loadOptions: LoadOptions,
): ColumnDef<ObjectRow>[] {
  const own = { ...writable, fields: writable.fields.filter((w) => w.type !== 'lookup') };
  return withColumnEditors(columns, own, loadOptions).map((column) =>
    column.editor?.kind === 'select' && own.fields.find((w) => w.key === column.key)?.type === 'boolean'
      ? { ...column, editor: { kind: 'boolean' } }
      : column,
  );
}

/** A list cell's save, as the API body: the column's writable field, typed for the wire. */
export function cellBody(columns: ColumnDef<ObjectRow>[], writable: Writable, columnKey: string, value: string | null): Record<string, unknown> {
  const column = columns.find((c) => c.key === columnKey);
  const w = column ? writableFor(column, writable) : undefined;
  if (!w) throw new ObjectsApiError(400, 'This field cannot be edited.');
  return { [w.key]: toWire(w, value) };
}

/** A detail field's save, as the API body: keyed by the writable field, typed for the wire. */
export function patchBody(
  metadata: ObjectMetadata<ObjectRow>,
  writable: Writable,
  fieldKey: string,
  value: string | null,
): Record<string, unknown> {
  const field = metadata.details?.find((f) => f.key === fieldKey);
  const w = (field && writableFor(field, writable)) ?? writable.fields.find((f) => f.key === fieldKey);
  if (!w) throw new ObjectsApiError(400, 'This field cannot be edited.');
  return { [w.key]: toWire(w, value) };
}

/** A form or editor string as the API takes it. */
export function toWire(field: WritableField, value: string | null): unknown {
  if (value === null || value.trim() === '') return null;
  if (field.type === 'number') return Number(value);
  if (field.type === 'boolean') return value === 'true';
  return value;
}

/** The New dialog's fields: every writable field, labelled as the record page labels it. */
export function formFields(
  metadata: ObjectMetadata<ObjectRow>,
  writable: Writable,
  loadOptions: LoadOptions,
): RecordFormField[] {
  return creatable(writable).fields.map((w) => {
    const detail = metadata.details?.find((f) => f.key === w.key || f.link?.idKey === w.key);
    const label = detail?.label ?? labelFromKey(w.key.replace(/_id$/, ''));
    return {
      key: w.key,
      label,
      editor: editorFor(w, loadOptions) as FieldEditorDef,
      ...(w.required ? { required: true } : {}),
      ...rulesOf(w),
      ...(detail?.help ? { labelHelp: detail.help } : {}),
      ...(w.multiline ? { multiline: true } : {}),
      ...(w.key === 'notes' || w.key === 'description' || w.key === 'summary' ? { wide: true } : {}),
    };
  });
}

/**
 * A related list's New form, opened on its parent: the parent lookup and what
 * the child inherits, as ids, and the names those lookups show.
 */
export function parentDefaults(
  parentMetadata: ObjectMetadata<ObjectRow>,
  parent: ObjectRow,
  foreignKey: string,
  inherit: Record<string, string>,
): { values: Record<string, string>; labels: Record<string, string> } {
  const values: Record<string, string> = { [foreignKey]: parent.id };
  const labels: Record<string, string> = {
    [foreignKey]: parentMetadata.titleOf?.(parent) ?? String(parent.name ?? ''),
  };
  for (const [field, parentField] of Object.entries(inherit)) {
    const value = parent[parentField];
    if (value === null || value === undefined || value === '') continue;
    values[field] = String(value);
    // The parent shows a lookup's name under its detail field's own key.
    const detail = parentMetadata.details?.find((f) => f.link?.idKey === parentField);
    const name = detail ? parent[detail.key] : undefined;
    if (name !== null && name !== undefined && name !== '') labels[field] = String(name);
  }
  return { values, labels };
}

/** The create body: the form's values typed for the wire, blanks left out. */
export function createBody(writable: Writable, values: Record<string, string | null>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const w of creatable(writable).fields) {
    const wire = toWire(w, values[w.key] ?? null);
    if (wire !== null) body[w.key] = wire;
  }
  return body;
}

/**
 * A clone's starting record: the source with every unique writable field
 * blank (a project's code, an account's domain, a primary or default flag),
 * so the copy asks for a new value instead of failing on Save.
 */
export function cloneSource(metadata: ObjectMetadata<ObjectRow>, writable: Writable, source: ObjectRow): ObjectRow {
  const copy: ObjectRow = { ...source };
  for (const w of writable.fields) {
    if (!w.unique) continue;
    copy[w.key] = null;
    // A lookup also shows its record's name under the detail field's own key.
    for (const field of metadata.details ?? []) if (field.link?.idKey === w.key) copy[field.key] = null;
  }
  return copy;
}

/**
 * A clone's create body: every writable field as the clone started
 * (`cloneSource`), with the page's values on top. The page keys a lookup by
 * its detail field, so each value is mapped to the field it writes.
 */
export function cloneBody(
  metadata: ObjectMetadata<ObjectRow>,
  writable: Writable,
  start: ObjectRow,
  values: Record<string, string | null>,
): Record<string, unknown> {
  const form: Record<string, string | null> = {};
  for (const w of writable.fields) {
    const value = start[w.key];
    form[w.key] = value === null || value === undefined ? null : String(value);
  }
  for (const [key, value] of Object.entries(values)) {
    const field = metadata.details?.find((f) => f.key === key);
    const w = (field && writableFor(field, writable)) ?? writable.fields.find((f) => f.key === key);
    if (w) form[w.key] = value;
  }
  return createBody(writable, form);
}

// ── Requests ──────────────────────────────────────────────────────────────

const recordPathOf = (object: ObjectKey, id: string) => `/api/objects/${encodeURIComponent(object)}/${encodeURIComponent(id)}`;

async function failure(response: Response, fallback: string): Promise<ObjectsApiError> {
  let message = fallback;
  let body: Record<string, unknown> = {};
  try {
    const payload: unknown = await response.json();
    if (isRecord(payload)) {
      body = payload;
      if (typeof payload.error === 'string' && payload.error !== '') message = payload.error;
    }
  } catch {
    // Not JSON: keep the fallback.
  }
  return new ObjectsApiError(response.status, message, body);
}

/** The account a new opportunity was refused for, and what it lacks, when the server says so. */
export interface AccountSizeNeeded {
  id: string;
  name: string;
  missing: ('employee_count' | 'annual_revenue')[];
}

export function accountSizeNeeded(error: unknown): AccountSizeNeeded | null {
  if (!(error instanceof ObjectsApiError) || error.body.code !== 'account_size_required') return null;
  const account = error.body.account;
  if (!isRecord(account) || typeof account.id !== 'string' || typeof account.name !== 'string' || !Array.isArray(account.missing)) return null;
  const missing = account.missing.filter((m): m is AccountSizeNeeded['missing'][number] => m === 'employee_count' || m === 'annual_revenue');
  return { id: account.id, name: account.name, missing };
}

/**
 * Asks for an account's missing Employees and Annual Revenue and saves them;
 * resolves true when saved. Set by AccountSizeHost (accountSize.tsx).
 */
let askAccountSize: ((need: AccountSizeNeeded) => Promise<boolean>) | null = null;
export function setAccountSizeAsker(ask: typeof askAccountSize): void {
  askAccountSize = ask;
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;
const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

/** Saves fields on one record; resolves to the record as it now is. */
export async function updateRecord(fetcher: Fetch, object: ObjectKey, id: string, body: Record<string, unknown>): Promise<ObjectRow> {
  const response = await fetcher(recordPathOf(object, id), json('PATCH', body));
  if (!response.ok) throw await failure(response, `Could not save (${response.status}).`);
  const record: unknown = await response.json();
  if (!isRecord(record) || typeof record.id !== 'string') throw new ObjectsApiError(response.status, 'The saved record came back in an unexpected shape.');
  return record as ObjectRow;
}

/** Creates a record; resolves to its id. */
export async function createRecord(fetcher: Fetch, object: ObjectKey, body: Record<string, unknown>): Promise<string> {
  let response = await fetcher(`/api/objects/${encodeURIComponent(object)}`, json('POST', body));
  if (!response.ok) {
    const error = await failure(response, `Could not create the record (${response.status}).`);
    // A new opportunity needs its account's size: ask for it, save it, and try once more.
    const need = accountSizeNeeded(error);
    if (!need || !askAccountSize || !(await askAccountSize(need))) throw error;
    response = await fetcher(`/api/objects/${encodeURIComponent(object)}`, json('POST', body));
    if (!response.ok) throw await failure(response, `Could not create the record (${response.status}).`);
  }
  const payload: unknown = await response.json();
  if (!isRecord(payload) || typeof payload.id !== 'string') throw new ObjectsApiError(response.status, 'The new record came back without an id.');
  return payload.id;
}

/** Deletes a record. */
export async function deleteRecord(fetcher: Fetch, object: ObjectKey, id: string): Promise<void> {
  const response = await fetcher(recordPathOf(object, id), { method: 'DELETE' });
  if (!response.ok) throw await failure(response, `Could not delete (${response.status}).`);
}

/** What a change made to many records came to: the ids it worked for, and why the rest failed. */
export interface ManyOutcome {
  done: string[];
  failed: { id: string; message: string }[];
}

/**
 * Runs one write per record (a bulk edit or delete from a list), a few at a
 * time. The API has no bulk endpoint; each record's own rules still apply,
 * so some may fail while the rest succeed.
 */
export async function forEachRecord(ids: string[], write: (id: string) => Promise<unknown>, concurrency = 6): Promise<ManyOutcome> {
  const outcome: ManyOutcome = { done: [], failed: [] };
  for (let i = 0; i < ids.length; i += concurrency) {
    const chunk = ids.slice(i, i + concurrency);
    const settled = await Promise.allSettled(chunk.map((id) => write(id)));
    settled.forEach((result, j) => {
      const id = chunk[j]!;
      if (result.status === 'fulfilled') outcome.done.push(id);
      else outcome.failed.push({ id, message: result.reason instanceof Error && result.reason.message ? result.reason.message : 'Failed.' });
    });
  }
  return outcome;
}

// ── Related lists ─────────────────────────────────────────────────────────

/** What one related list can add and remove (GET /meta's `relations`). */
export type Relation =
  | { kind: 'child'; object: string; foreignKey: string; inherit?: Record<string, string>; join?: boolean }
  | { kind: 'link'; object?: string };

/** The `relations` part of a /meta payload; malformed entries are left out. */
export function readRelations(payload: unknown): Record<string, Relation> {
  if (!isRecord(payload) || !isRecord(payload.relations)) return {};
  const out: Record<string, Relation> = {};
  for (const [key, value] of Object.entries(payload.relations)) {
    if (!isRecord(value)) continue;
    if (value.kind === 'child' && typeof value.object === 'string' && typeof value.foreignKey === 'string') {
      const child: Relation = { kind: 'child', object: value.object, foreignKey: value.foreignKey };
      // Child field → parent field, copied on New rather than asked for.
      if (isRecord(value.inherit)) {
        const inherit = Object.fromEntries(
          Object.entries(value.inherit).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
        );
        if (Object.keys(inherit).length > 0) child.inherit = inherit;
      }
      // A join row (a contact's role on a deal): its fields edit in place, and Remove keeps both records.
      if (value.join === true) child.join = true;
      out[key] = child;
    } else if (value.kind === 'link') {
      out[key] = typeof value.object === 'string' ? { kind: 'link', object: value.object } : { kind: 'link' };
    }
  }
  return out;
}

const relatedPathOf = (object: ObjectKey, id: string, relation: string) =>
  `${recordPathOf(object, id)}/related/${encodeURIComponent(relation)}`;

/** Links an existing record into a related list (an audience's members). */
export async function addLink(fetcher: Fetch, object: ObjectKey, id: string, relation: string, targetId: string): Promise<void> {
  const response = await fetcher(relatedPathOf(object, id, relation), json('POST', { id: targetId }));
  if (!response.ok) throw await failure(response, `Could not add (${response.status}).`);
}

/** Takes a record out of a related list without deleting it. */
export async function removeLink(fetcher: Fetch, object: ObjectKey, id: string, relation: string, targetId: string): Promise<void> {
  const response = await fetcher(`${relatedPathOf(object, id, relation)}/${encodeURIComponent(targetId)}`, { method: 'DELETE' });
  if (!response.ok) throw await failure(response, `Could not remove (${response.status}).`);
}
